import { InfoPage } from '@/components/info-page.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { Badge } from '@/components/ui/badge.js';
import { EmptyArray, getOrgs } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { validateRouteRole } from '@/lib/routing.js';
import { isAirAdmin } from '@airreading/common';
import { CreateOrg } from '@/routes/_layout/orgs/-components/create-org.js';
import {
  OrgWithSchoolsAndStats,
  OrgStatus,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
  orgStatusSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { ImportFromEdlink } from './-components/import-from-edlink.js';
import { schoolTabs } from './schools/-components/tabs.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { booleanFilterToSelectedValues, selectedValuesToBooleanFilter, usePaginatedFilters } from '@/lib/filtering.js';
import { z } from 'zod';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Button } from '@/components/ui/button.js';
import { ChevronDown } from 'lucide-react';
import { ImportOrgFromV1 } from './-components/import-org-chart-from-v1.js';
import { EdlinkIdCode } from '@/components/alphanumeric-code.js';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';

const orgsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional(),
  meetingType: z.array(videoConferenceTypeSchema.optional()).optional(),
  hasEdlinkId: z.boolean().optional(),
  statuses: z.array(orgStatusSchema).optional(),
});

const orgStatusLabels: Record<OrgStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export const Route = createFileRoute('/_layout/orgs/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: OrgsIndex,
  validateSearch: orgsSearchSchema,
});

const columnHelper = createColumnHelper<OrgWithSchoolsAndStats>();
const columns = [
  columnHelper.accessor((row) => row, {
    header: 'Name',
    id: 'org-name',
    cell: ({ getValue }) => (
      <Link to={'/orgs/$orgId'} params={{ orgId: getValue().id }}>
        {getValue().name}
      </Link>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    id: 'status',
    cell: ({ getValue }) => orgStatusLabels[getValue()],
  }),
  columnHelper.accessor('timeZone', {
    header: 'Time Zone',
  }),
  columnHelper.accessor('startPlainDate', {
    header: 'Start Date',
    id: 'startPlainDate',
    cell: ({ getValue }) => {
      const startDate = getValue();

      if (!startDate) return null;
      return <div className="min-w-[90px]">{startDate.toString()}</div>;
    },
  }),
  columnHelper.accessor('endPlainDate', {
    header: 'End Date',
    id: 'endPlainDate',
    cell: ({ getValue }) => {
      const endDate = getValue();

      if (!endDate) return null;
      return <div className="min-w-[90px]">{endDate.toString()}</div>;
    },
  }),
  columnHelper.accessor('totalEnrolledStudents', {
    header: 'Total Enrolled Students',
  }),
  columnHelper.accessor('videoConferenceType', {
    header: 'Meeting Type',
    id: 'video-conference-type',
    cell: ({ getValue }) => VideoConferenceTypeLabels[getValue()],
  }),
  columnHelper.accessor('schools', {
    header: 'Schools',
    id: 'customer-school',
    cell: ({ getValue }) => (
      <div className="flex flex-col gap-y-1">
        {(getValue() || EmptyArray).map((school) => (
          <Link to="/orgs/schools/$schoolId/schedules" params={{ schoolId: school.id }} key={school.id}>
            <Badge variant={'outline'} key={school.id} className="hover:bg-accent">
              {school.name}
            </Badge>
          </Link>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor('edlinkId', {
    header: 'Edlink ID',
    id: 'edlinkId',
    cell: ({ getValue }) => {
      const edlinkId = getValue();
      return edlinkId ? (
        <div className="flex items-center">
          <EdlinkIdCode code={edlinkId} wrapText={false} />
        </div>
      ) : (
        '-'
      );
    },
  }),
];

function OrgsIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      meetingType: [[meetingType, activeMeetingType], setMeetingType],
      hasEdlinkId: [[hasEdlinkId, activeHasEdlinkId], setHasEdlinkId],
      statuses: [[statuses, activeStatuses], setStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'meetingType', 'hasEdlinkId', 'statuses']);

  const {
    data: response,
    error,
    refetch,
  } = useQuery(
    getOrgs({
      pageIndex,
      pageSize,
      filters: {
        query: activeQuery,
        meetingType: activeMeetingType,
        hasEdlinkId: activeHasEdlinkId,
        statuses: activeStatuses,
      },
    }),
  );
  const orgs = useMemo(() => response?.orgs || EmptyArray, [response]);
  useErrorToast(error, 'Error loading districts. Please try again.');

  const table = useReactTable({
    data: orgs,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
    rowCount: response?.total || 0,
  });
  return (
    <InfoPage headerText="Schools" tabs={schoolTabs()}>
      <EntityTable
        table={table}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        showPagination={true}
        topBarRightComponents={
          <>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button className="ml-auto hidden h-8 lg:flex">
                  Add District
                  <ChevronDown className="mr-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[150px]">
                <CreateOrg
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add District</DropdownMenuItem>}
                />
                <ImportOrgFromV1 />
                <ImportFromEdlink
                  trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from Edlink</DropdownMenuItem>}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        filters={{
          filterLeftComponents: (
            <>
              <TableFilterInputField
                placeholder="Search district by name"
                value={query ?? ''}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <TableFilterDropdown
                title="Meeting Type"
                options={videoConferenceTypeSchema.options.map((value) => ({
                  label: VideoConferenceTypeLabels[value],
                  value,
                }))}
                selectedValues={meetingType || []}
                onChange={setMeetingType}
              />
              <TableFilterDropdown
                title="Status"
                data-testid="districts-filter-dropdown-status"
                options={orgStatusSchema.options.map((value) => ({
                  label: orgStatusLabels[value],
                  value,
                }))}
                selectedValues={statuses?.length ? statuses : [OrgStatus.active]}
                onChange={(values) => setStatuses(values.length ? values : undefined)}
              />
              <TableFilterDropdown
                title="Edlink"
                options={buildBooleanSelectOptions()}
                variant="radio"
                selectedValues={booleanFilterToSelectedValues(hasEdlinkId)}
                onChange={(vals) => setHasEdlinkId(selectedValuesToBooleanFilter(vals))}
              />
              <TableFilterResetButton show onClick={resetFilters} />
            </>
          ),
        }}
      />
    </InfoPage>
  );
}
